import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { NotificationCategory, UpdateNotificationPreferenceDto } from "@reactive-resume/dto";
import { useEffect, useRef } from "react";

import {
  archiveNotification,
  deleteNotification,
  fetchNotification,
  fetchNotificationPopups,
  fetchNotifications,
  fetchNotificationPreferences,
  fetchUnreadCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  updateNotificationPreference,
  type NotificationFilters,
} from "./api";
import { updateNotificationBadges } from "./badges";
import { fetchAdminQueues } from "./api";

export const NOTIFICATION_KEYS = {
  all: ["notifications"] as const,
  list: (filters: NotificationFilters = {}) => ["notifications", "list", filters] as const,
  unreadCount: ["notifications", "unread-count"] as const,
  preferences: ["notifications", "preferences"] as const,
  adminQueues: ["notifications", "admin-queues"] as const,
  popups: ["notifications", "popups"] as const,
};

/** Notifications non lues à afficher en fenêtre (`data.popup`). */
export const useNotificationPopups = (enabled = true) =>
  useQuery({
    queryKey: NOTIFICATION_KEYS.popups,
    queryFn: fetchNotificationPopups,
    enabled,
    staleTime: 30_000,
  });

/**
 * FILES D'ATTENTE OPS — agrégat « ce qui exige une action maintenant ».
 * Rafraîchi toutes les 30 s et invalidé par le pont temps réel dès qu'une
 * alerte admin arrive (retrait, demande d'aide, paiement), afin que les
 * badges de la sidebar et le widget « À traiter » ne mentent jamais.
 */
export const useAdminQueues = (enabled = true) =>
  useQuery({
    queryKey: NOTIFICATION_KEYS.adminQueues,
    queryFn: fetchAdminQueues,
    enabled,
    staleTime: 15_000,
    refetchInterval: enabled ? 30_000 : false,
  });

/** Liste paginée (infinite scroll) de la boîte de réception. */
export const useNotifications = (filters: NotificationFilters = {}) =>
  useInfiniteQuery({
    queryKey: NOTIFICATION_KEYS.list(filters),
    queryFn: ({ pageParam }) => fetchNotifications({ ...filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

/** Compteur de non-lues (badges UI + titre d'onglet + badge PWA). */
export const useUnreadCount = () => {
  const query = useQuery({
    queryKey: NOTIFICATION_KEYS.unreadCount,
    queryFn: fetchUnreadCount,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (typeof query.data?.count === "number") {
      updateNotificationBadges(query.data.count);
    }
  }, [query.data?.count]);

  return query;
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markNotificationAsRead,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });
    },
  });
};

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });
    },
  });
};

export const useArchiveNotification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, undo }: { id: string; undo?: boolean }) => archiveNotification(id, undo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });
    },
  });
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });
    },
  });
};

/** Préférences catégorie × canal (utilisées par l'UI de paramètres, Phase 1). */
export const useNotificationPreferences = () =>
  useQuery({
    queryKey: NOTIFICATION_KEYS.preferences,
    queryFn: fetchNotificationPreferences,
  });

/** Lecture d'une notification complète (page de détail). */
export const useNotification = (id: string) =>
  useQuery({
    queryKey: [...NOTIFICATION_KEYS.all, "detail", id] as const,
    queryFn: () => fetchNotification(id),
    enabled: Boolean(id),
  });

/**
 * Synchronisation « ouverture = lue ».
 * Le serveur marque la notification comme lue lors du GET /:id
 * (findOneByIdForUser) ; ce hook répercute l'état sur TOUS les caches
 * client (liste, compteur de non-lues, badge cloche/PWA) dès l'arrivée
 * de la donnée — sinon la cloche et la liste restent « non lue ».
 */
export const useSyncNotificationRead = (id: string, isRead: boolean | undefined) => {
  const queryClient = useQueryClient();
  const synced = useRef(false);

  useEffect(() => {
    if (!id || isRead === undefined || synced.current) return;
    synced.current = true;

    // État lu écrit immédiatement dans le cache du détail…
    queryClient.setQueryData(
      [...NOTIFICATION_KEYS.all, "detail", id] as const,
      (previous: { isRead?: boolean; readAt?: string | null } | undefined) =>
        previous
          ? { ...previous, isRead: true, readAt: previous.readAt ?? new Date().toISOString() }
          : previous,
    );

    // …puis liste + compteur (et badges via useUnreadCount) rafraîchis.
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all, refetchType: "all" });
  }, [id, isRead, queryClient]);
};

/**
 * Marquage optimiste « lue » dans tous les caches React Query
 * (pages de liste, détail) + revalidation du compteur de non-lues.
 * Appelé au clic sur une notification AVANT la navigation : la liste,
 * l'en-tête de compteur et les badges se mettent à jour instantanément,
 * pendant que le serveur confirme (GET /:id marque côté base).
 */
export const markNotificationReadInCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
) => {
  const readAt = new Date().toISOString();

  queryClient.setQueriesData({ queryKey: NOTIFICATION_KEYS.all }, (data: unknown) => {
    if (!data || typeof data !== "object") return data;

    // Page paginée { items, nextCursor }
    if ("items" in data && Array.isArray((data as { items: unknown[] }).items)) {
      const page = data as {
        items: Array<{ id: string; isRead?: boolean; readAt?: string | null }>;
        nextCursor?: string | null;
      };
      let changed = false;
      const items = page.items.map((notification) => {
        if (notification?.id === id && !notification.isRead) {
          changed = true;
          return { ...notification, isRead: true, readAt: notification.readAt ?? readAt };
        }
        return notification;
      });
      return changed ? { ...page, items } : data;
    }

    // Détail { id, isRead, readAt }
    const single = data as { id?: string; isRead?: boolean; readAt?: string | null };
    if (single.id === id && single.isRead === false) {
      return { ...single, isRead: true, readAt: single.readAt ?? readAt };
    }

    return data;
  });

  // Le compteur (badge cloche, titre d'onglet, badge PWA) se revalide.
  void queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.unreadCount });
};

export const useUpdateNotificationPreference = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      category,
      data,
    }: {
      category: NotificationCategory;
      data: UpdateNotificationPreferenceDto;
    }) => updateNotificationPreference(category, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.preferences });
    },
  });
};
